// Neighborhood-level suggestions. In SF we can use local neighborhood boundary
// data. Everywhere else, generate candidate local areas around the user's
// people/places, then ask Google for the best available neighborhood/district
// name during enrichment.
// Runs only on demand (it spends Places quota).

import { get, writable } from 'svelte/store';
import { friends, trips, suggestions, enabledAmenityKeys } from './state';
import {
  haversineKm,
  modeScore,
  tripScoreFromMinutes,
  amenityBlendedScore,
  HEURISTIC_SPEEDS,
  MODE_WEIGHTS,
} from './scoring';
import { computeAmenities } from './compute';
import { lookupNeighborhood, type NeighborhoodResult } from './api';
import { NEIGHBORHOODS, type Neighborhood } from './neighborhoods';
import type { Candidate, Friend, LatLng, Suggestion, Trip } from './types';

const DISPLAY_COUNT = 3; // outlines shown
const ENRICH_COUNT = 5; // hoods that get real amenity lookups per run
const MAX_FRIEND_KM = 4; // ignore hoods farther than this from your nearest person
const GRID_POOL_SIZE = 12;

/** Heuristic commute score (0–100) for a point, from distance to friends. */
function commuteHeuristic(point: LatLng, fr: Friend[]): number {
  const avgKm = fr.reduce((s, f) => s + haversineKm(point, f), 0) / fr.length;
  return (
    modeScore(avgKm * HEURISTIC_SPEEDS.walkMinPerKm, 'WALKING') * MODE_WEIGHTS.WALKING +
    modeScore(avgKm * HEURISTIC_SPEEDS.bikeMinPerKm, 'BICYCLING') * MODE_WEIGHTS.BICYCLING +
    modeScore(avgKm * HEURISTIC_SPEEDS.transitMinPerKm, 'TRANSIT') * MODE_WEIGHTS.TRANSIT +
    modeScore(avgKm * HEURISTIC_SPEEDS.driveMinPerKm, 'DRIVING') * MODE_WEIGHTS.DRIVING
  );
}

/** Heuristic trip score (0–100), or null if no trips set. */
function tripHeuristic(point: LatLng, tr: Trip[]): number | null {
  if (!tr.length) return null;
  const sum = tr.reduce((s, t) => {
    const km = haversineKm(point, t);
    const fastest = Math.min(km * HEURISTIC_SPEEDS.tripMinPerKm, km * HEURISTIC_SPEEDS.transitMinPerKm);
    return s + tripScoreFromMinutes(fastest);
  }, 0);
  return sum / tr.length;
}

/**
 * Blend commute / amenity / trip into an overall 0–100. With thin data (<3
 * friends) the commute signal is noisy, so intrinsic walkability (amenities)
 * carries more weight — this is what makes central, walkable hoods win.
 */
function blend(commute: number, amenity: number | null, trip: number | null, thin: boolean): number {
  const w = thin
    ? { commute: 0.3, amenity: 0.55, trip: 0.15 }
    : { commute: 0.55, amenity: 0.35, trip: 0.1 };
  let total = commute * w.commute;
  let used = w.commute;
  if (amenity != null) { total += amenity * w.amenity; used += w.amenity; }
  if (trip != null) { total += trip * w.trip; used += w.trip; }
  return Math.round(total / used);
}

interface Ranked {
  name?: string;
  centroid: LatLng;
  ring?: LatLng[];
  source?: string;
  commute: number;
  trip: number | null;
  prelim: number;
}

function circleRing(center: LatLng, radiusKm = 1.2, steps = 28): LatLng[] {
  const latRad = center.lat * Math.PI / 180;
  const latDelta = radiusKm / 111.32;
  const lngDelta = radiusKm / (111.32 * Math.max(0.2, Math.cos(latRad)));
  return Array.from({ length: steps }, (_, i) => {
    const a = (i / steps) * Math.PI * 2;
    return {
      lat: center.lat + Math.sin(a) * latDelta,
      lng: center.lng + Math.cos(a) * lngDelta,
    };
  });
}

function rankSfNeighborhoods(fr: Friend[], tr: Trip[]): Ranked[] {
  const thin = fr.length < 3;
  return NEIGHBORHOODS.map((hood): Ranked | null => {
    const c = hood.centroid;
    const nearest = Math.min(...fr.map((f) => haversineKm(c, f)));
    if (nearest > MAX_FRIEND_KM) return null;
    const commute = commuteHeuristic(c, fr);
    const trip = tripHeuristic(c, tr);
    return {
      name: hood.name,
      centroid: c,
      ring: hood.ring,
      source: 'dataset',
      commute,
      trip,
      prelim: blend(commute, null, trip, thin),
    };
  })
    .filter((r): r is Ranked => r != null)
    .sort((a, b) => b.prelim - a.prelim);
}

function rankGeneratedAreas(fr: Friend[], tr: Trip[]): Ranked[] {
  const thin = fr.length < 3;
  const lats = fr.map((f) => f.lat);
  const lngs = fr.map((f) => f.lng);
  const buf = 0.035; // roughly city-neighborhood scale
  const minLat = Math.min(...lats) - buf;
  const maxLat = Math.max(...lats) + buf;
  const minLng = Math.min(...lngs) - buf;
  const maxLng = Math.max(...lngs) + buf;
  const step = 0.006;
  const ranked: Ranked[] = [];

  for (let lat = minLat; lat <= maxLat; lat += step) {
    for (let lng = minLng; lng <= maxLng; lng += step) {
      const centroid = { lat, lng };
      const nearest = Math.min(...fr.map((f) => haversineKm(centroid, f)));
      if (nearest > MAX_FRIEND_KM || nearest < 0.15) continue;
      const commute = commuteHeuristic(centroid, fr);
      const trip = tripHeuristic(centroid, tr);
      ranked.push({
        centroid,
        ring: circleRing(centroid),
        commute,
        trip,
        prelim: blend(commute, null, trip, thin),
      });
    }
  }

  ranked.sort((a, b) => b.prelim - a.prelim);
  const picks: Ranked[] = [];
  for (const r of ranked) {
    if (picks.length >= GRID_POOL_SIZE) break;
    if (picks.every((p) => haversineKm(p.centroid, r.centroid) >= 1.2)) picks.push(r);
  }
  return picks;
}

function rankNeighborhoods(fr: Friend[], tr: Trip[]): Ranked[] {
  const sf = rankSfNeighborhoods(fr, tr);
  return sf.length ? sf : rankGeneratedAreas(fr, tr);
}

function toSuggestion(
  r: Ranked,
  rank: number,
  opts: { amenity?: number | null; enriched?: boolean; neighborhood?: NeighborhoodResult | null },
): Suggestion {
  const thin = get(friends).length < 3;
  const score = opts.enriched
    ? blend(r.commute, opts.amenity ?? null, r.trip, thin)
    : r.prelim;
  const name = opts.neighborhood?.name || r.name;
  return {
    lat: r.centroid.lat,
    lng: r.centroid.lng,
    score,
    rank,
    enriched: !!opts.enriched,
    neighborhoodName: name,
    neighborhoodSource: opts.neighborhood?.source || r.source,
    neighborhoodAddress: opts.neighborhood?.formattedAddress,
    ring: r.ring,
    commute: Math.round(r.commute),
    amenity: opts.amenity ?? null,
    trip: r.trip != null ? Math.round(r.trip) : null,
  };
}

let enrichGen = 0;

/** True while a suggestion run (which spends API quota) is in flight. */
export const suggestionsRunning = writable(false);

// ---- Local cache of the (quota-spending) enriched picks ----
//
// Enrichment hits the Places API, so we persist the final result to
// localStorage and restore it on reload instead of re-running. The cache is
// fingerprinted to the people/places it was computed from, so it self-
// invalidates the moment those inputs change (even via import, which doesn't go
// through the entity helpers that call clearSuggestions). Kept local-only —
// it's derived data, so it stays out of the synced state document.
const SUGGEST_STORAGE_KEY = 'sfhome:suggestions';

function inputFingerprint(fr: Friend[], tr: Trip[]): string {
  const key = (e: LatLng & { id: string }) => `${e.id}:${e.lat.toFixed(4)},${e.lng.toFixed(4)}`;
  // Amenity picks are part of the fingerprint: they change the enrichment score.
  const am = [...get(enabledAmenityKeys)].sort().join(',');
  return `f[${fr.map(key).sort().join('|')}]t[${tr.map(key).sort().join('|')}]a[${am}]`;
}

function persistSuggestions(list: Suggestion[]): void {
  try {
    localStorage.setItem(SUGGEST_STORAGE_KEY, JSON.stringify({
      fingerprint: inputFingerprint(get(friends), get(trips)),
      suggestions: list,
    }));
  } catch {
    /* quota exceeded or unavailable — picks just won't persist */
  }
}

/**
 * Reconcile the picks with the current people/places: restore the cache if it
 * still matches, otherwise drop stale picks (and the stale file). Safe to call
 * at hydration AND after a remote/cloud apply, which swaps inputs underneath us
 * without going through clearSuggestions.
 */
export function loadCachedSuggestions(): void {
  try {
    const current = inputFingerprint(get(friends), get(trips));
    const raw = localStorage.getItem(SUGGEST_STORAGE_KEY);
    const data = raw ? (JSON.parse(raw) as { fingerprint?: string; suggestions?: Suggestion[] }) : null;
    if (data && data.fingerprint === current && Array.isArray(data.suggestions) && data.suggestions.length) {
      suggestions.set(data.suggestions);
      return;
    }
    // No cache valid for these inputs — clear the stale file + any stale picks.
    if (raw) localStorage.removeItem(SUGGEST_STORAGE_KEY);
    if (get(suggestions).length) {
      enrichGen++; // cancel any in-flight enrichment for the old inputs
      suggestions.set([]);
    }
  } catch {
    /* corrupt/unavailable storage — leave suggestions as-is */
  }
}

/** Drop any existing picks — called when inputs change so stale picks don't mislead. */
export function clearSuggestions(): void {
  enrichGen++; // cancel any in-flight enrichment
  suggestions.set([]);
  try { localStorage.removeItem(SUGGEST_STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Run the neighborhood-suggestion pipeline ON DEMAND (it spends Places quota).
 * Shows heuristic picks instantly, then refines the top few with real amenity
 * data and re-ranks.
 */
export async function runSuggestions(): Promise<void> {
  const fr = get(friends);
  const tr = get(trips);
  if (!fr.length) {
    suggestions.set([]);
    return;
  }
  suggestionsRunning.set(true);
  const myGen = ++enrichGen;
  try {
    const ranked = rankNeighborhoods(fr, tr);
    if (!ranked.length) {
      suggestions.set([]);
      return;
    }

    // Instant heuristic preview (top 3 by prelim).
    suggestions.set(ranked.slice(0, DISPLAY_COUNT).map((r, i) => toSuggestion(r, i + 1, {})));

    // Enrich the top few with real amenity scoring, then re-rank.
    const enriched: Suggestion[] = [];
    for (const r of ranked.slice(0, ENRICH_COUNT)) {
      if (myGen !== enrichGen) return;
      let amenity: number | null = null;
      let neighborhood: NeighborhoodResult | null = null;
      try {
        const [amenities, area] = await Promise.all([
          computeAmenities(r.centroid),
          r.name ? Promise.resolve(null) : lookupNeighborhood(r.centroid.lat, r.centroid.lng),
        ]);
        amenity = amenityBlendedScore({ amenities } as Candidate, get(enabledAmenityKeys));
        neighborhood = area;
      } catch {
        /* keep heuristic-only for this hood */
      }
      enriched.push(toSuggestion(r, 0, { amenity, enriched: true, neighborhood }));
    }
    if (myGen !== enrichGen) return;

    enriched.sort((a, b) => b.score - a.score);
    const final = enriched.slice(0, DISPLAY_COUNT).map((s, i) => ({ ...s, rank: i + 1 }));
    suggestions.set(final);
    persistSuggestions(final); // cache the quota-spent result for next reload
  } finally {
    if (myGen === enrichGen) suggestionsRunning.set(false);
  }
}
